import { Injectable, Compiler } from '@angular/core';
import { Router, NavigationStart, NavigationError, NavigationEnd, ActivatedRoute, Data, NavigationCancel } from '@angular/router';
import { SpinnerService } from './spinner/spinner.service';
import { TitleService } from './title.service';
import { AppearanceService, DEFAULT_THEME } from './appearance.service';
import { EupToastService } from './toastr/eupToast.service';
import { navigationErrorPrefix } from '../../assets/consts';
import { TimberService } from '@logging/timber.service';
import { BiFeatureWrapperService } from '@logging/bi-feature-wrapper.service';
import { ApplicationNavigationService } from 'app/services/shell/application-navigation/application-navigation.service';
import { NavigationInfo } from '@shared/generalInterfaces';
import { Pages } from '@logging/interfaces/bi.enums';
import { PlatformLoginService } from 'app/services/platform-login/platform-login.service';

@Injectable()
export class RouterInterceptorService {
	private prevUrl: string;
	private navigationStartTime: number;
	excludedPages: string[] = [Pages.Home, Pages.Login, Pages.LoginLegacy];

	get previousRoute() {
		if (this.prevUrl && this.prevUrl !== '/') { return this.prevUrl; }
		return this.applicationNavigationService.peekTransition()?.urlFrom;
	}

	constructor(
		private router: Router,
		private compiler: Compiler,
		private spinner: SpinnerService,
		private titleService: TitleService,
		private activatedRoute: ActivatedRoute,
		private appearanceService: AppearanceService,
		private eupToastr: EupToastService,
		private timberService: TimberService,
		private biFeatureWrapperService: BiFeatureWrapperService,
		private applicationNavigationService: ApplicationNavigationService,
		private platformLoginService: PlatformLoginService
	) {}

	initialize(): void {
		this.router.events.subscribe((val: any) => {
			if (val instanceof NavigationStart) {
				this.handleNavigationStart(val);
			}

			if (val instanceof NavigationEnd) {
				this.handleNavigationEnd(val);
			}

			if (val instanceof NavigationError) {
				this.handleNavigationError(val);
			}

			if (val instanceof NavigationCancel) {
				this.handleNavigationCancel(val);
			}
		});
	}

	handleNavigationCancel(event: NavigationCancel) {
		this.spinner.stop();
		this.timberService.trace('Navigation canceled while navigating to route', {
			module: 'RouterInterceptorService',
			extendedParameters: { Url: event.url, Reason: event.reason },
		});
	}

	handleNavigationStart(event: NavigationStart) {
		this.prevUrl = this.getFullUrl();
		this.navigationStartTime = new Date().getTime();
		this.spinner.start(true);
		this.timberService.trace('Navigation start navigating to route', {
			module: 'RouterInterceptorService',
			extendedParameters: { Url: event.url, NavigationTrigger: event.navigationTrigger, Id: `${event.id}` },
		});
		this.handleTransition(this.prevUrl, event.url);
	}

	handleNavigationEnd(event: NavigationEnd) {
		this.spinner.stop();
		this.eupToastr.clearAllToastrs();

		// calculate timing
		if (this.navigationStartTime) {
			const timeElapsed = new Date().getTime() - this.navigationStartTime;
		}

		let route = this.activatedRoute;
		while (route.firstChild) {
			route = route.firstChild;
		}
		route.data.subscribe((data: Data) => {
			if (!data['skipTitle']) {
				this.titleService.setTitle(data['title']);
			}

			const theme = data['theme'] || DEFAULT_THEME;
			this.appearanceService.toTheme(theme);
			this.appearanceService.setResponsive(data['isResponsive']);
		});
		this.timberService.trace('Navigation end navigating to route', {
			module: 'RouterInterceptorService',
			extendedParameters: { Url: event.url, urlAfterRedirects: event.urlAfterRedirects, Id: `${event.id}` },
		});
	}

	// if any error happens during changing of routes (session expired / exception)
	// then redirect to login page - angular2 doesn't supply the http status of the response
	// so we can't act accordingly
	handleNavigationError(event: NavigationError) {
		this.spinner.stop();
		// angular cache failed component requests so we have to clear the cache
		// otherwise next time angular will request a component that failed to load
		// will throw exception without even making a request to the server
		this.compiler.clearCache();

		// error in login page - reload current page (angular2 doesn't support reloading components yet)
		if (event.url === '/home' && this.platformLoginService.getLoginRoutes().includes(this.prevUrl)) {
			window.location.reload();
		} else {
			// Remain on current page when error occurs loading new page
			this.router.navigateByUrl('/home', { skipLocationChange: true }).then(() => {
				this.router.navigate([this.prevUrl]);
				throw new Error(`${navigationErrorPrefix}: Cannot navigate to ${event.url} - Unexpected Error`);
			});
		}
	}

	private logBi(prevPage: string, newPage: string): void {
		this.timberService.uiNavigationEvent({
			sourceName: prevPage,
			targetName: newPage
		})
	}

	private handleTransition(prevUrl: string, newUrl: string): void {
		const prevPage = this.biFeatureWrapperService.extractPageFromString(prevUrl);
		const newPage = this.biFeatureWrapperService.extractPageFromString(newUrl);
		const prevTransition = this.applicationNavigationService.peekTransition();
		const prev = prevTransition?.navigatedToAppId ?? prevPage.page;
		const navState = this.router.getCurrentNavigation()?.extras?.state as Record<string, unknown> | undefined;

		if ((prevPage.page && prevPage.page !== newPage.page) ||
			 prevTransition?.urlFrom.toLowerCase() === newUrl) {
			const isBackNavigation = navState?.isBackNavigation === true ||
				prevTransition?.navigatedFromAppId === newPage.page ||
				prevTransition?.urlFrom.toLowerCase() === newUrl;

			if (isBackNavigation) {
				this.logBi(prev, this.toBiName(prevTransition.navigatedFromAppId));
				this.applicationNavigationService.popTransition();
			} else {
				this.logBi(prev, newPage.page);
				if (prevPage.wrapper === 'viewer-wrapper' && newPage.wrapper === 'viewer-wrapper') {
					this.replaceLastNavigation(this.toAppId(newPage.page));
				} else if (this.shouldPushTransition(prev, newPage.page)) {
					const transition = {
						navigatedFromAppId: this.toAppId(prev),
						navigatedToAppId: this.toAppId(newPage.page),
						urlFrom: prevUrl,
						appMetadata: navState ? JSON.stringify(navState) : undefined,
					} as NavigationInfo;
					this.applicationNavigationService.pushTransition(transition);
				}
			}
		}
	}

	private replaceLastNavigation(newPage: string): void {
		const lastTransition = this.applicationNavigationService.popTransition();
		const navState = this.router.getCurrentNavigation()?.extras?.state;
		this.applicationNavigationService.pushTransition({
				navigatedFromAppId: lastTransition.navigatedFromAppId,
				navigatedToAppId: newPage,
				urlFrom: lastTransition.urlFrom,
				appMetadata: navState ? JSON.stringify(navState) : lastTransition?.appMetadata,
			} as NavigationInfo
		);
	}

	private shouldPushTransition(prevPage: string, newPage: string): boolean {
		// on app load and refresh prevPage is undefined
		return prevPage && !this.excludedPages.includes(newPage);
	}

	// Convert BI page name to appId for navigation transitions
	// BI uses 'RxViewer' for analytics, but SL closing workflow needs 'rx-app'
	private toAppId(biName: string): string {
		return biName === 'RxViewer' ? 'rx-app' : biName;
	}

	// Convert appId back to BI page name for logging
	private toBiName(appId: string): string {
		return appId === 'rx-app' ? 'RxViewer' : appId;
	}

	/**
	 * Shell `Router.url` only reflects host routes. MFes have their own.
	 * Falls back to {@link Router.url} when not in a browser (e.g. tests / SSR).
	 */
	private getFullUrl(): string {
		if (typeof window === 'undefined' || !window.location) {
			return this.router.url;
		}
		const { pathname, search, hash } = window.location;
		return `${pathname}${search}${hash}`;
	}
}
